import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Alert,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import PaymentService, { Payment } from '../../services/payment.service';
import { NumberTextField } from '../../components/common';

const PaymentForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [isExpense, setIsExpense] = useState(false);

  const [formData, setFormData] = useState<Payment>({
    paymentNumber: '', // Auto-generated on save (PAY-YYYY-MM-####)
    invoice: undefined,
    paymentDate: new Date().toISOString().split('T')[0],
    amount: 0,
    paymentMethod: 'cash',
    reference: '',
    notes: '',
    receivedByName: '',
    isExpense: false,
    category: '',
    description: '',
    account: undefined,
  });

  useEffect(() => {
    if (isEdit && id) {
      fetchPayment(parseInt(id));
    }
  }, [id, isEdit]);

  const fetchPayment = async (paymentId: number) => {
    try {
      setLoading(true);
      const response = await PaymentService.getOne(paymentId);
      const paymentData = response.data?.data;

      if (paymentData) {
        const attrs = paymentData.attributes;
        const expenseMode = attrs.isExpense || false;
        setIsExpense(expenseMode);
        
        setFormData({
          paymentNumber: attrs.paymentNumber,
          invoice: attrs.invoice?.data?.id,
          paymentDate: attrs.paymentDate,
          amount: attrs.amount || 0,
          paymentMethod: attrs.paymentMethod || 'cash',
          reference: attrs.reference || '',
          notes: attrs.notes || '',
          receivedBy: attrs.receivedBy?.data?.id,
          receivedByName: attrs.receivedByName || '',
          isExpense: expenseMode,
          category: attrs.category || '',
          description: attrs.description || '',
          account: attrs.account?.data?.id,
        });
      }
    } catch (error) {
      console.error('Error fetching payment:', error);
      setError('Failed to load payment');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleExpenseCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setIsExpense(checked);
    setFormData({ 
      ...formData, 
      isExpense: checked,
      invoice: checked ? undefined : formData.invoice,
      description: checked ? formData.description : '',
    });
  };

  const validateForm = (): boolean => {
    if (isExpense) {
      if (!formData.description || formData.description.trim() === '') {
        setError('Please enter a description for the expense');
        return false;
      }
    }

    const amountNum = Number(formData.amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError('Please enter a valid amount');
      return false;
    }
    if (!formData.paymentMethod) {
      setError('Please select a payment method');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Don't send category when empty — Strapi enum rejects '' and only allows specific values
    const sanitizePayload = (data: Payment & { paymentNumber?: string }) => {
      const { category, ...rest } = data;
      const payload = { ...rest } as Partial<Payment>;
      if (category != null && category !== '') {
        payload.category = category;
      }
      return payload;
    };

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      if (isEdit && id) {
        await PaymentService.update(parseInt(id), sanitizePayload(formData));
        setSuccess(isExpense ? 'Expense updated!' : 'Payment updated!');
      } else {
        const createData = sanitizePayload({ ...formData, paymentNumber: '' }) as Payment;
        await PaymentService.create(createData);
        setSuccess(isExpense ? 'Expense recorded!' : 'Payment recorded!');
      }

      setTimeout(() => {
        navigate('/payments');
      }, 1500);
    } catch (error: any) {
      console.error('Error saving:', error);
      setError(
        error.response?.data?.error?.message ||
          `Failed to save ${isExpense ? 'expense' : 'payment'}. Please try again.`
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit 
          ? (isExpense ? 'Edit Expense' : 'Edit Payment')
          : (isExpense ? 'Record Expense' : 'Record Payment')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {/* Payment Information */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {isExpense ? 'Expense Details' : 'Payment Details'}
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              {/* Expense Checkbox */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isExpense}
                    onChange={handleExpenseCheckboxChange}
                    color="primary"
                  />
                }
                label="This payment is an expense"
              />

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {/* Payment number is auto-generated (PAY-YYYY-MM-####) — not shown */}
                <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }} {...({} as any)}>
                  <TextField
                    fullWidth
                    required
                    label={isExpense ? "Expense Date" : "Payment Date"}
                    name="paymentDate"
                    type="date"
                    value={formData.paymentDate}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </Box>

              {/* Expense-specific: Description only */}
              {isExpense && (
                <TextField
                  fullWidth
                  required
                  multiline
                  rows={3}
                  label="Description"
                  name="description"
                  value={formData.description || ''}
                  onChange={handleChange}
                  placeholder="Describe the expense..."
                />
              )}

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }} {...({} as any)}>
                  <NumberTextField
                    fullWidth
                    required
                    label="Payment Amount"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    inputProps={{ min: 0, step: 0.001 }}
                    helperText="Amount in OMR (up to 3 decimal places)"
                  />
                </Box>
                <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }} {...({} as any)}>
                  <TextField
                    select
                    fullWidth
                    required
                    label="Payment Method"
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleChange}
                  >
                    <MenuItem value="cash">Cash</MenuItem>
                    <MenuItem value="card">Card</MenuItem>
                    <MenuItem value="bankTransfer">Bank Transfer</MenuItem>
                    <MenuItem value="check">Check</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </TextField>
                </Box>
              </Box>

              <TextField
                fullWidth
                label="Reference / Transaction ID"
                name="reference"
                value={formData.reference}
                onChange={handleChange}
                helperText="Check number, transaction ID, or any reference"
              />

              <TextField
                fullWidth
                label="Received By"
                name="receivedByName"
                value={formData.receivedByName || ''}
                onChange={handleChange}
                placeholder="Name of person who received this payment"
              />

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Any additional notes about this payment..."
              />
            </Stack>
          </Paper>

          {/* Summary */}
          <Card variant="outlined" sx={{ bgcolor: isExpense ? 'error.light' : 'success.light' }}>
            <CardContent>
              <Typography variant="h5" color={isExpense ? 'error.contrastText' : 'success.contrastText'} gutterBottom>
                {isExpense ? 'Expense Summary' : 'Payment Summary'}
              </Typography>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1" color={isExpense ? 'error.contrastText' : 'success.contrastText'}>
                    Amount:
                  </Typography>
                  <Typography variant="h4" color={isExpense ? 'error.contrastText' : 'success.contrastText'} fontWeight="bold">
                    OMR {(Number(formData.amount) || 0).toFixed(3)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color={isExpense ? 'error.contrastText' : 'success.contrastText'}>
                    Method:
                  </Typography>
                  <Typography variant="body1" color={isExpense ? 'error.contrastText' : 'success.contrastText'}>
                    {formData.paymentMethod?.replace(/([A-Z])/g, ' $1').trim() || '-'}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color={isExpense ? 'error.contrastText' : 'success.contrastText'}>
                    Date:
                  </Typography>
                  <Typography variant="body1" color={isExpense ? 'error.contrastText' : 'success.contrastText'}>
                    {new Date(formData.paymentDate).toLocaleDateString()}
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/payments')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? 'Saving...' : isEdit 
                  ? (isExpense ? 'Update Expense' : 'Update Payment')
                  : (isExpense ? 'Record Expense' : 'Record Payment')}
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </form>
    </Box>
  );
};

export default PaymentForm;

